export type WorkspacePromptLinkLabel = {
  url: string;
  label: string;
};

function isSafeHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function trimTrailingUrlPunctuation(url: string): string {
  return String(url || '').replace(/[.,;:!?…]+$/g, '');
}

function linkOverlaps(
  links: Array<{ start: number; end: number }>,
  start: number,
  end: number
): boolean {
  return links.some((link) => start < link.end && end > link.start);
}

export function normalizePromptUrl(url: string): string {
  return String(url || '')
    .trim()
    .replace(/\/+$/, '')
    .toLowerCase();
}

export function extractPromptUrls(text: string): string[] {
  const source = String(text || '');
  const found: Array<{ start: number; end: number; url: string }> = [];
  const markdown = /\[([^\]\r\n]+?)\]\((https?:\/\/[^)\s]+)\)/gi;
  let match: RegExpExecArray | null;

  while ((match = markdown.exec(source))) {
    const url = match[2];
    if (!isSafeHttpUrl(url)) {
      continue;
    }

    found.push({
      start: match.index,
      end: match.index + match[0].length,
      url
    });
  }

  const bare = /https?:\/\/[^\s<>"'\]\)]+/gi;
  while ((match = bare.exec(source))) {
    const raw = trimTrailingUrlPunctuation(match[0]);
    if (!raw || !isSafeHttpUrl(raw)) {
      continue;
    }

    const start = match.index;
    const end = start + raw.length;
    if (linkOverlaps(found, start, end)) {
      continue;
    }

    found.push({ start, end, url: raw });
  }

  found.sort((left, right) => left.start - right.start);

  const urls: string[] = [];
  const seen = new Set<string>();
  for (const item of found) {
    const key = normalizePromptUrl(item.url);
    if (!key || seen.has(key)) {
      continue;
    }

    seen.add(key);
    urls.push(item.url);
  }

  return urls;
}

export function prunePromptLinkLabels(
  labels: WorkspacePromptLinkLabel[] | undefined,
  systemPrompt: string
): WorkspacePromptLinkLabel[] {
  const urls = new Set(extractPromptUrls(systemPrompt).map(normalizePromptUrl));
  const result: WorkspacePromptLinkLabel[] = [];
  const seen = new Set<string>();

  for (const item of labels || []) {
    const key = normalizePromptUrl(item.url);
    const label = String(item.label || '').trim();
    if (!key || !label || !urls.has(key) || seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push({
      url: item.url.trim(),
      label
    });
  }

  return result;
}

export function upsertPromptLinkLabel(
  labels: WorkspacePromptLinkLabel[] | undefined,
  url: string,
  label: string,
  systemPrompt: string
): WorkspacePromptLinkLabel[] {
  const key = normalizePromptUrl(url);
  const next = (labels || []).filter(
    (item) => normalizePromptUrl(item.url) !== key
  );

  const trimmed = label.trim();
  if (trimmed) {
    next.push({ url, label: trimmed });
  }

  return prunePromptLinkLabels(next, systemPrompt);
}

export function promptLinkLabelForUrl(
  labels: WorkspacePromptLinkLabel[] | undefined,
  url: string
): string {
  const key = normalizePromptUrl(url);
  const match = (labels || []).find(
    (item) => normalizePromptUrl(item.url) === key
  );
  return match?.label || '';
}
